import { useMemo } from 'react';
import { ExportImageMode } from '~/_shared/types/exportImage/exportImage';
import { useSelector } from '~/_shared/utils/hooks/useSelector';
import { mapSettingsConcurrencyIsOfflineSelector } from '~/store/mapSettings/concurrency/mapSettingsConcurrency.selectors';
import { mapSettingsExportImageSettingsSelector } from '~/store/mapSettings/toolsState/exportImageSettings/exportImageSettings.selectors';
import { testingModeEnabled } from '~/testingMode/testingMode';
import { type AppState } from '../app.store';
import { usePrimarySpreadsheetId } from './usePrimarySpreadsheetId';

const isMapInfoLoadingSelector = (state: AppState) => state.map.mapInfo.isLoading;
export const useIsMapInfoLoadingSelector = () => useSelector(isMapInfoLoadingSelector);

const isReadOnlyMapSelector = (state: AppState) => !!state.map.mapInfo.data?.isReadOnly;
export const useIsReadOnlyMapSelector = () => useSelector(isReadOnlyMapSelector);

export const isMapPresentationalSelector = (state: AppState) => state.map.mapInfo.isPresentational;
export const useIsMapPresentationalSelector = () => useSelector(isMapPresentationalSelector);

export const isMapEditDisabled = (state: AppState) => isMapPresentationalSelector(state) || isReadOnlyMapSelector(state);
export const useIsMapEditDisabled = () => useSelector(isMapEditDisabled);

export const isMapOverlayUiHiddenSelector = (state: AppState) => {
  const exportImageMode = mapSettingsExportImageSettingsSelector(state)?.mode;
  return isMapPresentationalSelector(state) && (exportImageMode === ExportImageMode.Map || exportImageMode === ExportImageMode.MapAndLegend);
};
export const useIsMapOverlayUiHidden = () => useSelector(isMapOverlayUiHiddenSelector);

const googleSheetsSpreadsheetsInfo = (state: AppState) => state.map.mapInfo.data?.googleSheets;
export const useGoogleSheetsSpreadsheetsInfo = () => useSelector(googleSheetsSpreadsheetsInfo);

export const isMapInOfflineModeSelector = (state: AppState) =>
  isMapPresentationalSelector(state) || mapSettingsConcurrencyIsOfflineSelector(state) || testingModeEnabled() || isReadOnlyMapSelector(state);

export const usePrimaryGoogleSpreadsheetInfo = () => {
  const primarySpreadsheetId = usePrimarySpreadsheetId();
  const googleSheetsSpreadsheetsInfo = useGoogleSheetsSpreadsheetsInfo();

  return useMemo(() => {
    if (googleSheetsSpreadsheetsInfo && primarySpreadsheetId) {
      const primaryGoogleSheetSpreadsheet = googleSheetsSpreadsheetsInfo.find(
        i => i.spreadsheetId === primarySpreadsheetId
      );
      if (primaryGoogleSheetSpreadsheet && primaryGoogleSheetSpreadsheet.realSpreadsheets.length) {
        return primaryGoogleSheetSpreadsheet.realSpreadsheets[0];
      }
    }
    return null;
  }, [googleSheetsSpreadsheetsInfo, primarySpreadsheetId]);
};
